
import {useCallback, useMemo, MouseEvent, useState} from 'react';
import SvgIcon from '@/components/common/svgIcon';
import {withDoctorboxWebview} from '@/utils/navigate';
import {isDoctorApp} from '@/utils/utils';
import * as Logger from '@/utils/ubcUtils';
import {useSearchParams} from '@/hooks/router';

import styles from './card.less';

export interface CardProps {

    /**
     * 标题
     */
    title: string;

    /**
     * 作者
     */
    author: string;

    /**
     * 视频时长
     */
    duration: string;

    /**
     * 封面图
     */
    coverPicture: string;

    /**
     * 票数
     */
    voteNumber: number;

    /**
     * 投票状态
     */
    status: 1 | 2 | 3;

    /**
     * fieldValue
     */
    fieldValue: any;

    /**
     * 当前项ID
     */
    itemId: number;

    /**
     * 投票类型
     */
    voteType: string;

    onVote: (itemId: number, callback: (status: 'success' | 'error') => void) => void;
}

function Card(props: CardProps) {

    const {
        status,
        itemId,
        fieldValue,
        onVote,
        voteType,
    } = props;

    const {
        url,
        number,
        title,
        author,
        duration,
        voteInfo,
        coverPicture,
    } = fieldValue || {};

    const headerStyle = {
        backgroundImage: `url(${coverPicture})`,
    };

    const [voteCount, setVoteCount] = useState<number>(voteInfo?.voteCount || 0);
    const [voteStatus, setVoteStatus] = useState(false);
    const query = useSearchParams();

    const handleSubmitVote = useCallback(
        (event: MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();

            Logger.clk({
                page: 'vote_index',
                value: 'vote_clk',
                ext: {
                    // eslint-disable-next-line camelcase
                    sf_ref: query.channel,
                    id: query.id,
                    type: title,
                    // eslint-disable-next-line camelcase
                    op_id: itemId,
                },
            });

            if (voteStatus || status === 3) {
                return;
            }
            setVoteStatus(true);
            onVote && onVote(itemId, (status: 'success' | 'error') => {
                status === 'success' && setVoteCount(voteCount + 1);
                setVoteStatus(false);
            });
        },
        [voteStatus, status, onVote, itemId, voteCount]
    );

    const handleItemClick = useCallback(
        () => {
            Logger.clk({
                page: 'vote_index',
                value: 'option_clk',
                ext: {
                    // eslint-disable-next-line camelcase
                    sf_ref: query.channel,
                    id: query.id,
                    type: title,
                    // eslint-disable-next-line camelcase
                    op_id: itemId,
                },
            });
        },
        [itemId, query.channel, query.id, title]
    );

    const renderOperation = useMemo(
        () => {
            return (
                <div className={styles.operation}>
                    {
                        [
                            status === 1 ? (
                                <div className={styles(styles.operatonBtn, styles.noStart)}>活动未开始</div>
                            ) : null,
                            status === 2 || status === 3 ? (
                                <div className={styles.voteBar}>
                                    <div className={styles.voteCount}>
                                        <span className={styles.number}>{voteCount}</span>
                                        {voteType === 'vote' ? '票' : '赞'}
                                    </div>
                                    <div
                                        className={styles(styles.operatonBtn, styles.processing, {
                                            [styles.over]: status === 3,
                                            [styles.requesting]: voteStatus,
                                        })}
                                        onClick={handleSubmitVote}
                                    >
                                        {status === 2 ? (voteType === 'vote' ? '投票' : '点赞') : '已结束'}
                                    </div>
                                </div>
                            ) : null,
                        ]
                    }
                </div>
            );
        },
        [handleSubmitVote, status, voteCount, voteStatus]
    );

    return (
        <a className={styles.voteCard} href={isDoctorApp() ? withDoctorboxWebview(url) : url} onClick={handleItemClick}>
            <div
                style={headerStyle}
                className={styles.header}
            >
                <div className={styles.labels}>
                    <span className={styles.index}>编号{number}</span>
                    {voteInfo?.rank ? <span className={styles.rank}>排名{voteInfo?.rank}</span> : null}
                </div>
                {
                    duration && (
                        <div className={styles.duration}>
                            <SvgIcon name="play" className={styles.play} />
                            <span>{duration}</span>
                        </div>
                    )
                }
            </div>

            <div className={styles.content}>
                <div className={styles(styles.title, 'c-line-clamp2')}>{title}</div>
                {
                    author
                        ? (
                            <div className={styles(styles.author, 'c-line-clamp1')}>
                                <span>作者：</span>
                                <span className="c-line-clamp1">{author}</span>
                            </div>
                        ) : null
                }
            </div>
            {renderOperation}
        </a>
    );
}

export default Card;
